import { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import UI Components
import { BusyLoader, SkeletonLoader } from '@geneui/components';
// Import Components
import GeneralInfo from 'components/BonusModal/GeneralInfo';
import BonusCalculationLogic from './BonusCalculationLogic';
// Import Services
import { TemplateHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useOneSpinRates from 'hooks/useOneSpinRates';
// Import Constants
import { BonusTriggerType, l } from 'constants/common';
// Import Context
import { useVersionHistoryContext } from 'components/VersionHistory';
// Import Enums
import { AwardTypesEnum } from 'enums';

const CampaignBonusInfoModalBody = ({ campaignBonus, bonusInfo, isLoading }) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();

    const [internalMessageTitle, setInternalMessageTitle] = useState<Nullable<string>>(null);

    const { pointInTime } = useVersionHistoryContext();

    const { getTemplateRequest } = useCancelableRequests({
        getTemplateRequest: TemplateHttpService.getTemplate,
    });

    const isBonusTriggerTypeManual = campaignBonus?.TriggerType === BonusTriggerType.Manual;

    const init = () => {
        if (!isNil(campaignBonus?.InternalTemplateId)) {
            doGetRequest(getTemplateRequest.request, {
                queryString: { templateId: campaignBonus.InternalTemplateId, pointInTime },
                successCallback: (data) => {
                    setInternalMessageTitle(data?.Name);
                },
            });
        }
    };

    const { rates: oneSpinRates, isLoading: isLoadingSpinRates } = useOneSpinRates({
        bonusType: bonusInfo?.Type,
        configJson: campaignBonus.ConfigJson,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <BusyLoader isBusy={isLoading} type="spinner" spinnerSize="medium">
            <div className="crm-scrollable-container">
                <GeneralInfo
                    id={bonusInfo?.Id}
                    title={bonusInfo?.Name}
                    description={bonusInfo?.Description}
                    type={bonusInfo?.Type}
                    startDate={bonusInfo?.StartDate}
                    endDate={bonusInfo?.EndDate}
                    state={bonusInfo?.State}
                    walletType={bonusInfo?.BalanceType}
                    internalMessageTitle={internalMessageTitle}
                    usageType={bonusInfo?.BonusDetails[0]?.Usage}
                    isLocalNetwork={bonusInfo?.BonusDetails[0]?.IsLocalNetwork}
                />
                <div className="crm-object-modal-head">
                    {isBonusTriggerTypeManual && (
                        <div className="title-wrapper">
                            <p className="title-wrapper-text">{t(l.BonusCalculationLogic)}</p>
                            <span className="title-wrapper-line" />
                        </div>
                    )}
                    <BonusCalculationLogic campaignBonus={campaignBonus} bonusInfo={bonusInfo} isLoading={isLoading} />
                </div>
                {[AwardTypesEnum.FreeSpin, AwardTypesEnum.WonderWheelSpin].includes(bonusInfo?.Type) && (
                    <div>
                        <div className="crm-object-modal-head">
                            <div className="title-wrapper">
                                <p className="title-wrapper-text">{t(l.OneSpinPrice)}</p>
                                <span className="title-wrapper-line" />
                            </div>
                            <SkeletonLoader isBusy={isLoadingSpinRates}>
                                {oneSpinRates.map(({ Currency, Amount }, index) => {
                                    return (
                                        <div className="key-value-h" key={`${Currency} + ${index}`}>
                                            <span className="ellipsis-text kv-label">{Currency}</span>
                                            <span className="ellipsis-text kv-value kv-value-css">{Amount}</span>
                                        </div>
                                    );
                                })}
                            </SkeletonLoader>
                        </div>
                    </div>
                )}
            </div>
        </BusyLoader>
    );
};

CampaignBonusInfoModalBody.propTypes = {
    campaignBonus: PropTypes.object,
    bonusInfo: PropTypes.object,
    isLoading: PropTypes.bool.isRequired,
};

export default memo(CampaignBonusInfoModalBody);
